import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy, HostListener, ElementRef } from '@angular/core';
import { ChatbotService } from 'src/services/chatbot-services/chatbot.service';
import { LoggerService } from 'src/services/logger-services/logger.service';
import { NewsItem, ApiResponse } from '../../models/news-updates.models';

@Component({
  selector: 'app-news-updates',
  templateUrl: './news-updates.component.html',
  styleUrls: ['./news-updates.component.scss']
})
export class NewsUpdatesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() newsItems: NewsItem[] = [];
  @Input() isresponseLoading: boolean = false; // Add loading state input
  @Input() isNewsLoading: boolean = false; // Add loading state input
  @Input() errorMessage: string = '';
  @Input() newsAndUpdatesnodata: boolean = false;
  @Input() isWebsocketCompleteAndDataSaved: boolean = false;// Track when websocket is complete and data is saved
  @Input() tenantId: string | null = null;
  @Input() domainId: string | null = null;
  @Input() userId : string | null =null; 
  @Input() news_feeds_id: number | null = null;
  @Output() linkClick = new EventEmitter<string>();

  public isLoading: boolean = false;

  // Search and sort properties
  searchQuery: string = '';
  selectedSortOption: string = '';
  isSortDropdownOpen: boolean = false;
  sortOptions: string[] = ['Date'];

  // Pagination properties
  currentPage: number = 1;
  pageSize: number = 5; // Changed to match the default selected value in template
  pageSizeOptions: number[] = [5, 10, 15, 20];
  isDropdownOpen: boolean = false;

  private readonly NewsUpdatesComponentError = (error: any)=>
    `[NewsUpdatesComponent] ${JSON.stringify(error)}`;

  constructor(
    private readonly elementRef: ElementRef,
    public chatbotService: ChatbotService,
    private readonly loggerService: LoggerService
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
      this.isSortDropdownOpen = false;
    }
  }

  // Search and sort methods
  onSearchInput(): void {
    // Reset to first page when searching
    this.currentPage = 1;
    
      this.filterNewsFeeds(this.userId, this.domainId, this.tenantId, this.news_feeds_id, this.searchQuery, this.selectedSortOption);
  }

  toggleSortDropdown(): void {
    this.isSortDropdownOpen = !this.isSortDropdownOpen;
  }

  onSortOptionChange(option: string): void {
    this.selectedSortOption = option;
    let selectedSortOptionValue =""
    if (this.selectedSortOption == "Date"){
       selectedSortOptionValue = "date"
    }
    this.isSortDropdownOpen = false;
    // Reset to first page when sorting
    this.currentPage = 1;
    
    this.filterNewsFeeds(this.userId, this.domainId, this.tenantId, this.news_feeds_id, this.searchQuery, this.selectedSortOption);
  }

  // Helper method to determine if search and sort controls should be shown
  shouldShowSearchAndSort(): boolean {
    return this.isWebsocketCompleteAndDataSaved && this.newsItems && this.newsItems.length > 0;
  }

  getDomainFromUrl(url: string): string {
    try {
      const domain = new URL(url).hostname;
      return domain.replace('www.', '');
    } catch {
      return url;
    }
  }

  getLinkText(url: string): string {
    if (url.length > 50) {
      return url.slice(0,50) + '....';
    }
    return url;
  }

  onLinkClick(url: string): void {
    this.linkClick.emit(url);
  }

  // Pagination methods
  get paginatedNewsItems(): NewsItem[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.newsItems.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.newsItems.length / this.pageSize);
  }

  get startIndex(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endIndex(): number {
    const end = this.currentPage * this.pageSize;
    return Math.min(end, this.newsItems.length);
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      // Force update to ensure UI reflects the change
      this.forcePaginationUpdate();
    }
  }

  onPageSizeChange(event: Event): void {
    try{
    const select = event.target as HTMLSelectElement;
    const newPageSize = parseInt(select.value);
    
    // Only update if the page size actually changed
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1; // Reset to first page when changing page size
      
      // Force change detection by triggering a recalculation
      this.forcePaginationUpdate();
      
      // Additional validation to ensure pagination is correct
      if (this.totalPages < 1) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    }
  }
  catch(error: any){
    this.loggerService.logError(this.NewsUpdatesComponentError(error), "onPageSizeChange")
    return;
  }
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  onPageSizeChangeCustom(newPageSize: number): void {
    try{
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1; // Reset to first page when changing page size
      this.isDropdownOpen = false; // Close dropdown after selection
      
      // Force change detection by triggering a recalculation
      this.forcePaginationUpdate();
      
      // Additional validation to ensure pagination is correct
      if (this.totalPages < 1) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    }
  }
  catch(error: any){
    this.loggerService.logError(this.NewsUpdatesComponentError(error), "onPageSizeChangeCustom")
    return;
  }
  }

  ngOnInit(): void {
    // Initialize pagination when component loads
    this.forcePaginationUpdate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset to first page when news items change
    if (changes['newsItems'] && !changes['newsItems'].firstChange) {
      this.currentPage = 1;
      this.forcePaginationUpdate();
    }
  }

  ngOnDestroy(): void {
    // Clean up any resources if needed
    // Currently no cleanup needed
  }

  private forcePaginationUpdate(): void {
    // This method ensures the pagination updates properly
    // The getter methods will recalculate when called
    const _ = this.paginatedNewsItems;
    const __ = this.totalPages;
  }
  
  public filterNewsFeeds(
    userId:string|null,
    domainId: string | null,
    tenantId: string | null,
    newsFeedsIds: number | null,
    searchTerm: string = "",
    sortBy: string = ""
  ): void {
    try{
    this.isLoading =true
    this.chatbotService.searchNewsFeeds(
      userId!,
      tenantId!,
      domainId!,
      newsFeedsIds??[],
      searchTerm,
      sortBy,
    ).subscribe({
      next: (response: ApiResponse) => this.handleMetadataResponse(response),
      error: (error: any) => this.handleMetadataError(error)
    })
  }
  catch(error: any){
    this.loggerService.logError(this.NewsUpdatesComponentError(error), "filterNewsFeeds")
    return;
  }
  }

  public handleMetadataResponse(response: ApiResponse): void {
      this.isLoading = false;
  
      if (
        response.status === 200 &&
        response.body?.data &&
        typeof response.body.data === 'object'
      ) {
        this.newsItems = Object.values(response.body.data);
  
  
      } else {
        this.newsItems = [];
        this.loggerService.logError(this.NewsUpdatesComponentError(response), "handleMetadataResponse")
      }
    }

    public handleMetadataError(error: any): void {
    this.isLoading = false;
    this.loggerService.logError(this.NewsUpdatesComponentError(error), "handleMetadataError")
    this.newsItems = [];
  }
} 